import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';

import { FunctionGroupModel } from '@app/Models/WebApiModels';
import ApiService from '@app/Services/ApiService';
import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';

import ReactSelect, { ReactSelectValue, ReactSelectSize } from '@app/Components/ReactSelect';
import { AzureAuth, UserPermissions } from '../AzureAuth';
import { applyDecorators } from "@app/Helpers/Decorator";

export enum CrewFunctionPickerMode {
    All,
    Group,
    Cabin,
    Cockpit,
    Ground,
    CabinCockpit,
    FcmRoster,
    MarshalFseManage
}

type CrewFunctionPickerProps = {
    clearable?: boolean;
    multi?: boolean;
    value?: string | null;
    placeholder?: string;
    onSelect?: (value: string | null) => void;
    viewMode?: CrewFunctionPickerMode;
    selectSize?: ReactSelectSize;
};

type PersonFunctionDTO = {
    id: string;
    name: string;
    parentId?: string;
};

@observer
export default class CrewFunctionPicker extends React.Component<CrewFunctionPickerProps> {
    private _store: CrewFunctionPickerStore;

    constructor(props: CrewFunctionPickerProps) {
        super(props);
        applyDecorators(this);
        this._store = new CrewFunctionPickerStore();
    }

    componentDidMount(): void {
        void this._store.loadFunctions(this.props.viewMode);
    }

    render() {
        const { clearable, selectSize, multi, value, placeholder } = this.props;
        return <ReactSelect placeholder={placeholder} options={this._options} multi={multi} value={value} isClearable={clearable} selectSize={selectSize} onSelect={this._onChangeHandler} />;
    }

    @action.bound
    private _onChangeHandler(value: ReactSelectValue) {
        this.props.onSelect && this.props.onSelect(value);
    }

    private get _options() {
        return this._availableFunctions.map((func) => {
            return { value: func.id, label: func.name };
        });
    }

    private get _availableFunctions() {
        const { viewMode } = this.props;
        const { functions } = this._store;
        switch (viewMode) {
            case CrewFunctionPickerMode.Group:
                return functions.filter((el) => !el.parentId && el.id !== FunctionGroupIds.Ground || el.parentId === FunctionGroupIds.Ground);
            case CrewFunctionPickerMode.Cabin:
                return functions.filter((x) => FunctionGroupIds.cabinIdWithGroupsIds.includes(x.id));
            case CrewFunctionPickerMode.Cockpit:
                return functions.filter((x) => FunctionGroupIds.cockpitWithGroupIds.includes(x.id));
            case CrewFunctionPickerMode.CabinCockpit:
                return functions.filter((x) => FunctionGroupIds.cabinIdWithGroupsIds.includes(x.id) || FunctionGroupIds.cockpitWithGroupIds.includes(x.id));
            case CrewFunctionPickerMode.Ground:
                return functions.filter((x) => FunctionGroupIds.groundWithGroupsIds.includes(x.id));
            case CrewFunctionPickerMode.MarshalFseManage:
                return functions.filter((x) => FunctionGroupIds.marshalsWithGroupsIds.includes(x.id) || FunctionGroupIds.fseWithGroupsIds.includes(x.id));
            default:
                return functions;
        }
    }
}

class CrewFunctionPickerStore {
    @observable functions: PersonFunctionDTO[] = [];

    constructor() {
        applyDecorators(this);
    }

    @action
    public async loadFunctions(mode?: CrewFunctionPickerMode) {
        const functionsGroups = await ApiService.getTypedData<FunctionGroupModel[]>(ApiUrls.FunctionGroupUrl);
        const funcList: PersonFunctionDTO[] = [];
        functionsGroups.data.forEach((group) => {
            funcList.push({
                id: group.id,
                name: `-${group.name} group-`
            });
            if (group.functions.length > 1) {
                group.functions.map((f) => {
                    funcList.push({
                        id: f.id,
                        name: f.name,
                        parentId: group.id
                    });
                });
            }
        });

        if (mode === CrewFunctionPickerMode.FcmRoster) {
            funcList.splice(6, 0, {
                id: FunctionGroupIds.Fcm,
                name: `-All (Cabin & Cockpit)-`
            });
        }

        this.functions = this.__hasAccessToReadFunction(funcList, mode);
    }

    private __hasAccessToReadFunction(functions: PersonFunctionDTO[], mode?: CrewFunctionPickerMode) {
        let funcList: PersonFunctionDTO[] = [...functions];

        if (mode === CrewFunctionPickerMode.MarshalFseManage){
            const hasAccessToFse = AzureAuth.hasAccess(UserPermissions.FseInfoRead);
            if (!hasAccessToFse) {
                funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Fse);
            }
            const hasAccessToMarshals = AzureAuth.hasAccess(UserPermissions.MarshalsInfoRead);
            if (!hasAccessToMarshals) {
                funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Marshals && x.parentId !== FunctionGroupIds.Marshals);
            }
            return funcList;
        }

        const hasAccessToCabin = AzureAuth.hasAccess(UserPermissions.RosterCabinRead);
        if (!hasAccessToCabin) {
            funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Cabin && x.id !== FunctionGroupIds.Fcm && x.parentId !== FunctionGroupIds.Cabin);
        }
        const hasAccessToCockpit = AzureAuth.hasAccess(UserPermissions.RosterCockpitRead);
        if (!hasAccessToCockpit) {
            funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Cockpit && x.id !== FunctionGroupIds.Fcm && x.parentId !== FunctionGroupIds.Cockpit);
        }
        const hasAccessToGround = AzureAuth.hasAccess(UserPermissions.RosterGroundRead);
        if (!hasAccessToGround) {
            funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Ground && x.parentId !== FunctionGroupIds.Ground);
        }
        const hasAccessToFse = AzureAuth.hasAccess(UserPermissions.RosterFseRead);
        if (!hasAccessToFse) {
            funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Fse);
        }
        const hasAccessToMarshals = AzureAuth.hasAccess(UserPermissions.RosterMarshalRead);
        if (!hasAccessToMarshals) {
            funcList = funcList.filter((x) => x.id !== FunctionGroupIds.Marshals && x.parentId !== FunctionGroupIds.Marshals);
        }

        return funcList;
    }
}
